import React, { useEffect } from 'react';
import {connect, useDispatch} from 'react-redux';
import {Routes, Route, Navigate, useNavigate} from 'react-router-dom';
import {
  createTheme,
  ThemeProvider,
  StyledEngineProvider,
} from '@mui/material/styles';
import PropTypes from 'prop-types';

import appConfig from '@/config';
import Loading from '@/components/organisms/Loading';
import Dialog from '@/components/organisms/Dialog';
import Scroll from '@/components/organisms/Scroll';
import Help from '@/components/templates/Help';
import Modal from '@/components/organisms/Modal';
import Login from '@/pages/Login';
import ChangePassword from '@/pages/Login/changePassword.jsx';
import authOperations from '@/reducks/auth/operations';

// ダッシュボード
import BFU50101G from '@/pages/bfu/BFU50101G';

// 共架申込
import BFA10101G from '@/pages/bfa/BFA10101G';
import BFA10102G from '@/pages/bfa/BFA10102G';
import BFA10103G from '@/pages/bfa/BFA10103G';
import BFA10104G from '@/pages/bfa/BFA10104G';
import BFA10109G from '@/pages/bfa/BFA10109G';

// ダミーページ
import DemoPage from '@/pages/DemoPage';

// Consoleを使用しない環境は、各メソッドの処理を空の処理で上書き
if (!appConfig.consolelog) {
  const emptyFunc = () => {};

  for (const key in console) {
    if (!Object.hasOwn(console, key)) {
      continue;
    }

    // error以外の関数を無効化
    if (typeof console[key] === 'function' &&
        key !== 'error') {
      console[key] = emptyFunc;
    }
  }
  console.log.disabled = true;
}

const themeDefault = createTheme({
  typography: {
    fontFamily: [
      '"Hiragino Kaku Gothic ProN"',
      '"Hiragino Sans"',
      '"Meiryo UI"',
      '"Noto Sans JP"',
      'sans-serif',
    ].join(','),
  },

  // 読み取り専用テキストフィールドの背景色
  components: {
    MuiInputBase: {
      styleOverrides: {
        input: {
          padding: '4px 4px 5px',
          '&[readonly]': {
            backgroundColor: '#F0F0F0',
          },
        },
      },
    },
  },
});

const themes = {
  default: themeDefault,
};

// 業務ページ
const pages = [

  // 共架申込
  { path: '/bfa10101g',             element: <BFA10101G /> },
  { path: '/bfa10102g',             element: <BFA10102G /> },
  { path: '/bfa10102g/:order/:rev', element: <BFA10102G /> },
  { path: '/bfa10103g/:order/:rev', element: <BFA10103G /> },
  { path: '/bfa10104g/:order/:rev', element: <BFA10104G /> },
  { path: '/bfa10109g/:order/:rev', element: <BFA10109G /> },
];

// ログアウト
const Logout = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(authOperations.doLogoutOperation(navigate));
  }, [navigate, dispatch]);
  return null;
};

// ルーティング
const routes = ({theme}) => (
  <StyledEngineProvider injectFirst>
    <ThemeProvider theme={themes[theme]}>
      <Loading />
      <Scroll />
      <Modal content={<Help />} />
      <Dialog />
      <Routes>

        {/* トップページ */}
        <Route path="/"          element={TopPage} />
        <Route path="/bfu50101g" element={TopPage} />

        {/* ログイン */}
        <Route path="/login" element={<Login/>} />

        {/* ログアウト */}
        <Route path="/logout" element={<Logout/>} />

        {/* パスワード変更 */}
        <Route path="/password"
          element={
            <RequireAuth withUserInfo={false}>
              <ChangePassword />
            </RequireAuth>
          }
        />

        {/* 業務ページ */}
        {
          pages.map((page) => (
            <Route path={page.path}
              key={'route-' + page.path}
              element={
                <RequireAuth>
                  {page.element}
                </RequireAuth>
              }
            />
          ))
        }

        {/* その他 - ダミーページ */}
        <Route path="*"
          element={
            <RequireAuth>
              <DemoPage />
            </RequireAuth>
          }
        />
      </Routes>
    </ThemeProvider>
  </StyledEngineProvider>

);

//------------------------------------------------------------------------------
// 要ログイン認証
//------------------------------------------------------------------------------
const RequireAuthComponent = ({
  children,
  isLoggedIn,
  userInfo,
  withUserInfo = true,
}) => {

  // ログイン認証未ならログインページへ
  if (!isLoggedIn) {
    console.log('Not logged in', location.pathname);
    return <Navigate replace to="/login" />;
  }

  // ユーザー情報なしならログインページへ
  if (withUserInfo && !userInfo) {
    console.log('No login user info', location.pathname);
    return <Navigate replace to="/login" />;
  }
  return children;
};

RequireAuthComponent.propTypes = {
  children: PropTypes.object,
  isLoggedIn: PropTypes.bool.isRequired,
  userInfo: PropTypes.object,
  withUserInfo: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  isLoggedIn: state.auth.isLoggedIn,
  userInfo: state.auth.userInfo,
});
const mapDispatchToProps = {};
const RequireAuth = connect(
    mapStateToProps,
    mapDispatchToProps,
)(RequireAuthComponent);

//------------------------------------------------------------------------------
// トップページ (ダッシュボード)
//------------------------------------------------------------------------------
const TopPage = <RequireAuth><BFU50101G /></RequireAuth>;

//------------------------------------------------------------------------------
// エクスポート
//------------------------------------------------------------------------------
const mapStateToPropsForMui = (state) => ({
  theme: state.style.theme,
});
const RoutesComponent = connect(mapStateToPropsForMui)(routes);
export default RoutesComponent;
